import type { Order, Sku } from '@commercelayer/sdk'

import z from 'zod'
import snakeCase from 'lodash/snakeCase'
import {
  cartItemSchema,
  ecommerceCartSchema,
} from '@integration-layer/schemas/GAEvents/cart'
import { extractCategoriesUnlocalized } from '@design-system/utils/formatAlgoliaHierarchicalCategories'
import { calculateGADiscountAmount } from '@design-system/utils/googleAnalyticsEcommerce'

type EcommerceCartSchema = z.infer<typeof ecommerceCartSchema>
type CartItem = z.infer<typeof cartItemSchema>
type AlgoliaObject = z.infer<typeof algoliaProductsSchema>

/**
 * Parameters for mapping Google Analytics ecommerce data.
 *
 * @typedef {Object} MapDataParams
 * @property {Order} cart - The cart/order to map
 * @property {Array<AlgoliaObject|null>} algoliaObjects - Algolia product objects
 * @property {number} [qty] - Optional quantity override
 */
type MapDataParams = {
  cart: Order
  algoliaObjects: Array<AlgoliaObject | null>
  qty?: number
}

/**
 * Provides utilities for mapping ecommerce data to Google Analytics format.
 *
 * This composable helps transform cart and product data into a standardized
 * format for Google Analytics tracking, handling various transformations and
 * data enrichment.
 *
 * @returns {Object} An object with methods for mapping ecommerce data
 * @property {Function} mapData - Maps cart and product data to GA format
 *
 * @example
 * const { mapData } = useMapGAEcommerceData()
 * const gaEcommerceData = await mapData({
 *   cart: myCart,
 *   algoliaObjects: myAlgoliaProducts
 * })
 */
export const useMapGAEcommerceData = () => {
  const { currentAlgoliaIndex: algoliaIndex } = useAppConfig()

  /**
   * Fetches Algolia product objects by their SKU codes.
   *
   * @param {string[]} codes - Array of SKU codes
   * @returns {Promise<Array<AlgoliaObject>>} Fetched Algolia product objects
   * @throws {Error} If no products are found
   * @private
   */
  const getAlgoliaObjects = async (codes: string[]) => {
    if (!codes.length) return
    const response = await $fetch(`/api/getProducts/${algoliaIndex}`, {
      query: {
        mfcList: codes.map(code => getSkuWithoutSize(code)).join(','),
      },
    })
    const products = response.filter(obj => !!obj)
    if (!products.length) throw new Error('products not found')
    return products
  }

  /**
   * Maps cart and product data to Google Analytics ecommerce tracking format.
   *
   * Transforms line items, calculates prices, applies discounts, and enriches
   * product data with Algolia information.
   *
   * @param {MapDataParams} params - Parameters for mapping data
   * @returns {Promise<EcommerceCartSchema>} Mapped ecommerce data for GA
   *
   * @example
   * const gaData = await mapData({
   *   cart: myCart,
   *   algoliaObjects: myAlgoliaProducts,
   *   actionFromChat: false
   * })
   */
  const mapData = async ({ cart, algoliaObjects, qty }: MapDataParams) => {
    const lineItems =
      cart?.line_items?.filter(({ item_type }) => item_type === 'skus') ?? []
    if (!algoliaObjects?.length && lineItems.length) {
      try {
        const SKUs = lineItems.reduce<string[]>((acc, { sku_code }) => {
          if (sku_code) acc.push(sku_code)
          return acc
        }, [])
        algoliaObjects = (await getAlgoliaObjects(SKUs)) ?? []
      } catch {
        return
      }
    }

    if (!lineItems.length) return

    const items = lineItems.map((lineItem, index) => {
      const algoliaObject = algoliaObjects?.find(
        obj => obj?.objectID === getSkuWithoutSize(lineItem.sku_code)
      )

      const item = lineItem?.item as Sku
      const brand = algoliaObject?.Brand ?? ''

      // 'discount_float' is the discount value applied on bulk to the line items in the cart (i.e. using a coupon code)
      const orderDiscount =
        typeof lineItem?.discount_float === 'number' &&
        typeof lineItem?.quantity === 'number' &&
        lineItem.quantity > 0
          ? Math.abs(lineItem.discount_float) / lineItem?.quantity
          : 0

      // This represents the price variation for the specific sku (i.e. during black friday sales)
      const skuPriceDiscount = calculateGADiscountAmount({
        newPrice: lineItem?.unit_amount_float,
        originalPrice: lineItem?.compare_at_amount_float,
      })

      const discount = Math.max(0, orderDiscount + skuPriceDiscount)

      const isAvailable =
        item?.inventory?.available && item?.inventory?.quantity > 0

      // unit_amount_float already has pricelist discounts applied, but we need to add special line item discounts too
      const price = Math.max(
        0,
        (lineItem?.unit_amount_float ?? 0) - orderDiscount
      )

      const { category1, category2, category4 } = extractCategoriesUnlocalized(
        algoliaObject?.hierarchicalCategories || [],
        algoliaObject?.categoryPageId2 || []
      )
      /** Tracks whether the first touch point for the current line item has been Powerfront Inside chat */
      const productFromChat =
        lineItem?.metadata?.product_from_chat === true ? 1 : 0

      const { pdpPath } = useRouteHelper()
      return {
        item_id: algoliaObject?.objectID ?? '',
        item_name: snakeCase(lineItem.name ?? ''),
        affiliation: 'armani.com',
        coupon: cart.coupon_code ?? '',
        discount,
        index: index + 1,
        item_brand:
          brand.toLowerCase() === 'ea7'
            ? 'EA7'
            : snakeCase(brand ?? 'armani group'),
        item_category: snakeCase(category1),
        item_category2: snakeCase(category2),
        item_category4: snakeCase(category4),
        item_list_id: '',
        item_list_name: snakeCase(`${brand}-${category2}`),
        item_variant: lineItem.metadata?.color ?? '',
        price,
        quantity: qty ?? lineItem.quantity,
        item_availability: isAvailable ? 1 : 0,
        item_size: lineItem.metadata?.size ?? '',
        item_reservable: 0,
        item_discount: discount ? 1 : 0,
        item_mfc_id: algoliaObject?.MFC ?? '',
        item_img: lineItem.image_url ?? '',
        item_client_service: productFromChat,
        item_url: useRequestURL().origin + pdpPath(algoliaObject),
      } satisfies CartItem
    })

    const totalPrice = items
      .map(item => item.price * item.quantity)
      .reduce((acc, curr) => acc + curr, 0)

    return {
      client_service: items.some(item => item.item_client_service === 1)
        ? 1
        : 0,
      currency: lineItems?.[0]?.currency_code ?? 'EUR',
      value: totalPrice,
      items,
    } satisfies EcommerceCartSchema
  }

  return { mapData }
}
